/**
 * analytics-shared/api/sharesApi.js
 *
 * Frontend client for the wedge-3 analytics sharing endpoints.
 *
 *   Asset shares (under /api/v1):
 *     POST   /analytics-shares                grant / upsert
 *     PATCH  /analytics-shares/:shareId       update access / expiry / active
 *     DELETE /analytics-shares/:shareId       revoke
 *     GET    /reports/:id/shares              shares for a report
 *     GET    /dashboards/:id/shares           shares for a dashboard
 *     GET    /analytics-shares/shared-with-me discover assets shared with me
 *
 *   Principal search (under /api, served by the users module):
 *     GET /users?search=&limit=
 *     GET /groups
 *     GET /roles
 *
 * Two different axios instances on purpose: `analyticsClient` is rooted at
 * /api/v1 (the wedge-1/2/3 surface), while user/group/role lookups live on
 * the legacy /api prefix served by the existing users module.
 */
import { analyticsClient, crmClient, unwrap } from './client';

// Backend asset type constants — keep these aligned with
// backend/modules/analytics_sharing/models/share_models.py.
export const ASSET_REPORT = 'report';
export const ASSET_DASHBOARD = 'dashboard';

// Access level constants. Labels live in the UI layer so we don't bake
// translation strings into the API client.
export const ACCESS_READ = 'read';
export const ACCESS_EDIT = 'edit';
export const ACCESS_MANAGE = 'manage';

export const PRINCIPAL_USER = 'user';
export const PRINCIPAL_GROUP = 'group';
export const PRINCIPAL_ROLE = 'role';

export const sharesApi = {
  // ─── Shares for a specific asset ────────────────────────────────────────
  listForReport: async (reportId) => analyticsClient
    .get(`/reports/${encodeURIComponent(reportId)}/shares`)
    .then(unwrap),

  listForDashboard: async (dashboardId) => analyticsClient
    .get(`/dashboards/${encodeURIComponent(dashboardId)}/shares`)
    .then(unwrap),

  listForAsset: async (assetType, assetId) => {
    if (assetType === ASSET_REPORT) return sharesApi.listForReport(assetId);
    if (assetType === ASSET_DASHBOARD) return sharesApi.listForDashboard(assetId);
    throw new Error(`Unknown asset type: ${assetType}`);
  },

  // ─── Grant / Update / Revoke ────────────────────────────────────────────
  // grant() is idempotent on the (asset, principal) tuple — POSTing a
  // second time with a different access_level updates the existing row.
  grant: async ({
    assetType, assetId, principalType, principalId, accessLevel = ACCESS_READ,
    reason, expiresAt,
  }) => analyticsClient
    .post('/analytics-shares', {
      asset_type: assetType,
      asset_id: assetId,
      shared_with_type: principalType,
      shared_with_id: principalId,
      access_level: accessLevel,
      reason,
      expires_at: expiresAt,
    })
    .then(unwrap),

  update: async (shareId, patch) => analyticsClient
    .patch(`/analytics-shares/${encodeURIComponent(shareId)}`, {
      access_level: patch.accessLevel,
      reason: patch.reason,
      expires_at: patch.expiresAt,
      is_active: patch.isActive,
    })
    .then(unwrap),

  revoke: async (shareId) => analyticsClient
    .delete(`/analytics-shares/${encodeURIComponent(shareId)}`)
    .then(unwrap),

  // ─── Discover ────────────────────────────────────────────────────────────
  sharedWithMe: async ({ assetType } = {}) => analyticsClient
    .get('/analytics-shares/shared-with-me', {
      params: assetType ? { asset_type: assetType } : {},
    })
    .then(unwrap),
};

// ─── Principal search (users / groups / roles) ────────────────────────────
// Lives in the same file so the ShareModal can `import { principalsApi }`
// from one place rather than juggling user/group/role clients separately.
// Returns a normalised `{ id, label, sublabel?, type }` shape so the modal's
// suggestion list doesn't have to know which collection each row came from.
const normaliseUser = (u) => ({
  type: PRINCIPAL_USER,
  id: u.id,
  label: u.display_value || u.name
    || `${u.first_name || ''} ${u.last_name || ''}`.trim()
    || u.email || u.id,
  sublabel: u.email,
  inactive: u.is_active === false,
});
const normaliseGroup = (g) => ({
  type: PRINCIPAL_GROUP,
  id: g.id,
  label: g.name,
  sublabel: g.member_count
    ? `${g.member_count} member${g.member_count === 1 ? '' : 's'}`
    : g.group_type,
});
const normaliseRole = (r) => ({
  type: PRINCIPAL_ROLE,
  id: r.id,
  label: r.name,
  sublabel: r.description,
});

export const principalsApi = {
  searchUsers: async (q, limit = 25) => crmClient
    .get('/users', { params: { search: q, limit } })
    .then(unwrap)
    .then((rows) => (rows || []).map(normaliseUser)),

  listGroups: async () => crmClient
    .get('/groups')
    .then(unwrap)
    .then((rows) => (rows || []).map(normaliseGroup)),

  listRoles: async () => crmClient
    .get('/roles')
    .then(unwrap)
    .then((rows) => (rows || []).map(normaliseRole)),
};
